/**
 * The eight background "looks" a person can pick in Settings → Appearance.
 *
 * Twelve backgrounds exist in total: four looks come in both a dark and a light
 * version (Tide, Dusk, Ember, Graphite/Paper) and four are one mode only. The
 * actual gradients live in src/styles/looks.css (one source of truth) and are
 * keyed on `[data-look][data-theme]`; the picker tiles reuse them by carrying
 * the same two attributes, so a tile can never drift from the real background.
 */

export type ColorMode = 'dark' | 'light';

export type LookId =
  | 'tide' | 'dusk' | 'ember' | 'graphite'
  | 'lagoon' | 'harbour' | 'nocturne' | 'apricot';

export interface Look {
  id: LookId;
  label: string;
  /** Set for the four coloured looks: the mode this look always uses. */
  forcedMode?: ColorMode;
}

export const LOOKS: Look[] = [
  { id: 'tide',     label: 'Tide' },
  { id: 'dusk',     label: 'Dusk' },
  { id: 'ember',    label: 'Ember' },
  { id: 'graphite', label: 'Graphite / Paper' },
  { id: 'lagoon',   label: 'Lagoon',   forcedMode: 'dark' },
  { id: 'harbour',  label: 'Harbour',  forcedMode: 'light' },
  { id: 'nocturne', label: 'Nocturne', forcedMode: 'dark' },
  { id: 'apricot',  label: 'Apricot',  forcedMode: 'light' },
];

export const DEFAULT_LOOK: LookId = 'tide';

export function isLookId(v: unknown): v is LookId {
  return typeof v === 'string' && LOOKS.some(l => l.id === v);
}

export function getLook(id: LookId): Look {
  return LOOKS.find(l => l.id === id) ?? LOOKS[0];
}

/** The mode actually shown: a coloured look decides for itself; a paired look
 *  uses the person's own remembered choice. */
export function effectiveMode(look: LookId, userMode: ColorMode): ColorMode {
  return getLook(look).forcedMode ?? userMode;
}

// ─── The browser's copy, PER PERSON ──────────────────────────────────────────
//
// ⛔ The browser copy used to be ONE pair of keys (`cbops_look`,
// `pulsehq_color_mode`) for whoever used the browser. A second person signing in on
// the same browser was painted the first person's look, and kept it if they had no
// saved look of their own. The keys now carry the person's id, so a browser copy can
// only ever be painted for the person it belongs to.
//
// ⚠️ `public/index.html` repeats this logic in plain ES5, because it runs before any
// bundle has loaded. `__tests__/ThemeContext.flip.test.tsx` runs the REAL inline script
// against the same storage fixtures as `currentUserIdFromStorage`, so the two cannot
// drift apart silently.

export const LOOK_KEY_PREFIX = 'cbops_look:';
export const MODE_KEY_PREFIX = 'pulsehq_color_mode:';
export const lookKey = (uid: string) => LOOK_KEY_PREFIX + uid;
export const modeKey = (uid: string) => MODE_KEY_PREFIX + uid;

/** Supabase keeps the session as `sb-<project ref>-auth-token`. */
const SESSION_KEY = /^sb-[a-z0-9]+-auth-token$/;

/**
 * Who is signed in, read SYNCHRONOUSLY from the stored session — so the very first paint
 * can already be that person's look. Null when nobody is signed in, when the stored
 * session is unreadable, or when more than one session is stored (it cannot then tell
 * whose it is). Never throws: a broken session must cost a default look, not the page.
 */
export function currentUserIdFromStorage(storage: Storage = localStorage): string | null {
  try {
    let found: string | null = null;
    let count = 0;
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (!k || !SESSION_KEY.test(k)) continue;
      count += 1;
      const parsed = JSON.parse(storage.getItem(k) || 'null');
      const id = parsed && parsed.user && parsed.user.id;
      found = typeof id === 'string' && id ? id : null;
    }
    return count === 1 ? found : null;
  } catch {
    return null;
  }
}

/** This person's browser copy, or null for each half that is missing or not valid. */
export function readBrowserCopy(uid: string | null, storage: Storage = localStorage): {
  look: LookId | null; mode: ColorMode | null;
} {
  if (!uid) return { look: null, mode: null };
  try {
    const look = storage.getItem(lookKey(uid));
    const mode = storage.getItem(modeKey(uid));
    return {
      look: isLookId(look) ? look : null,
      mode: mode === 'light' || mode === 'dark' ? mode : null,
    };
  } catch {
    return { look: null, mode: null };
  }
}
